import { Optional } from "ng/src/app/shared/model/app.model";
import { NoteColumn, NotesTableDataRow } from "./notes-table/notes-table.model";
import { FilterMatchMode, MessageService, SortMeta } from "primeng/api";
import { LogService } from "../logs/log.service";
import { Utils } from "../core/utils";
import { catchError, exhaustMap, finalize, map, mergeMap, share, switchMap, tap } from "rxjs/operators";
import { EMPTY, forkJoin, Observable, of, Subscription, throwError } from "rxjs";
import { NoteService } from "./shared/note.service";
import { Inject, Injectable, OnDestroy } from "@angular/core";
import { Note, NoteResponse } from "./shared/note.model";
import { AppStateService } from "../core/services/app-state.service";
import { ILocationService } from "angular";
import { NotesTableComponent } from "ng/src/app/notes/notes-table/notes-table.component";
import { ShiftsService } from "ng/src/app/shifts/shifts.service";
import { NotesTableService } from "./notes-table/notes-table.service";
import { NoteActionType } from "ng/src/app/notes/note-actions/note-actions.model";
import { NoteProjections } from "app/shared/model/request-parameter.model";
import { SignoffService } from "ng/src/app/notes/signoffs/signoff.service";
import { DisplayNote } from "ng/src/app/notes/note-display/note-display.model";

@Injectable()
export class EnterNotesTableDelegate implements OnDestroy {
    
    constructor(
	    private appStateService: AppStateService,
		private noteService: NoteService,
		private messageService: MessageService,
		private shiftsService: ShiftsService,
		private signoffService: SignoffService,
		@Inject('$location') private $location: ILocationService
	) {

		const defaultSorts = NotesTableService.getDefaultSorts(),
			showAcknowledgmentsColumn = LogService.getConfigValueFor(`${this.noteView}NotesShowAcknowledgements`) == 'true';

		this.columnKeys = [
            new NoteColumn('priority', undefined, defaultSorts.some(sm => sm.field == 'maxPriority')),
			new NoteColumn('createdBy', 'Source'),
			new NoteColumn('updatedBy', undefined, defaultSorts.some(sm => sm.field == 'updatedBy')),
            new NoteColumn('note'),
            new NoteColumn('acknowledgments', undefined, showAcknowledgmentsColumn)
		];
		
		this.getNotes(true);
    }
	
	ngOnDestroy() {
		Object.values(this.subscriptions).forEach(sub => sub?.unsubscribe());
	}

	columnKeys: NoteColumn[];
	hasMoreNotes = true;
	lastUpdate?: { date: Date, success: boolean };
	loading = false;
	notes: NotesTableDataRow[] = [];
	notesMessage!: string;
	params: Note.Query & { sort: string[] } = { sort: [] };

	subscriptions: {
		[key: string]: Subscription | undefined,
		notesSubscription?: Subscription,
		notesPoll?: Subscription,
        pendingSignoff?: Subscription
    } = {};
 
	private table?: NotesTableComponent;
    private page: number = 0;
    private _poll$?: Observable<NotesTableDataRow[]>;
    private minTimeframe = -1
	private updatedOrTimeframeEnded?: string;
    private _showPendingSignoffOnly = false
	private _signoffCount$?: Observable<number>;
	private firstSignoffNoteTime: string | undefined = undefined;
    
    get poll$(): Observable<NotesTableDataRow[]> {
        return this._poll$ = (this._poll$ ?? this.createBasePoll())
    }
    
    get signoffCount$(): Observable<number> {
        return this._signoffCount$ = this._signoffCount$ ?? this.createSignoffCountPoll();
    }
	
	get showPendingSignoffOnly() {
		return this._showPendingSignoffOnly
	}
	set showPendingSignoffOnly(showing: boolean) {
		if (this._showPendingSignoffOnly === showing) return
		
		this._showPendingSignoffOnly = showing
		
		if (showing)
			this.params.signoffPendingFor = this.appStateService.currentUser?.id
		else
			delete this.params.signoffPendingFor
		
		this.getNotes(true)
	}

    get filterReportId(): number | undefined | null {
    	return this.table?.activeNoteFilterReportId
	}
	
	get turnoverFilter(): boolean {
		return LogService.getConfigValueFor(`${this.noteView}NotesTurnoverOnly`) == 'true';
	}

    get usingTimeframeDesc(): boolean {
		return this.params.sort.length === 1 && this.params.sort[0] === 'timeframe,desc';
    }

    get noteView(): string {
        return AppStateService.urlSearchParamsHas('readOnlyView') ? 'recent' : 'enter';
    }

    get preferencesKey(): string {
        return `${this.logId}-${this.noteView}-notes`;
    }

    private get logId(): number {
        return LogService.selectedLog?.id ?? -1;
	}
	
	
  private lastId: Optional<number>;
    
  afterViewInit(table: NotesTableComponent) {
    this.table = table;
    
    this.resetParams();
    this.createPoll();
    this.getNotes(true, undefined, (success, notes) => {
	    if (notes != null && notes.length > 0) {
		    this.lastId = notes[notes.length - 1]?.id;
	    }
    });
  }
	
	onNoteActionEvent(update: {type: NoteActionType, note: Note}) {
		const { type, note } = update;
		const refresh = () => {
			return this.refreshNote(note).pipe(
				map((note) => {
					if (!note) return null;
					const idx = this.notes.findIndex(n => n.id === note.id);
					if (idx < 0) return null;
					return {
						idx: idx,
						note: NotesTableDataRow.fromNotes([note])[0]
					};
				}))
		}
		
		switch (type) {
			case "COMMENT":
				refresh().subscribe((update) => {
					if (!update) return;
					const { idx, note } = update;
					this.notes.splice(idx, 1, note);
				});
				break;
			case "DETAIL":
				refresh().pipe(
					switchMap((update) => {
						if (!update) return EMPTY;
						if (!this.showPendingSignoffOnly) return of(update);
						return this.signoffService.noteNeedsSignoffFromUser(update.note.id)
							.pipe(
								map(needsSignoff => {
									return {
										...update,
										needsSignoff
									}
								})
							)
					})
				)
					.subscribe(update => {
						if (!update) return;
						if ('needsSignoff' in update && !(update as any).needsSignoff) {
							this.notes.splice((update as any).idx, 1);
							return;
						}
						const { idx, note } = update;
						this.notes.splice(idx, 1, note);
					});
				break;
		}
	}

    onSortChanged(event: { multisortmeta: SortMeta[] }) {
        const newSort = event.multisortmeta.map(event => {
            const field = (event.field === 'updatedBy' || event.field === 'createdBy') ? (event.field + '.lastName') : event.field,
                dir = event.order == 1 ? 'asc' : 'desc';

            return `${field},${dir}`;
        });

        if (!newSort.reduce((changed, field) => (changed || this.params.sort.findIndex(f => f === field) < 0), false))
            return;

        this.params.sort = newSort;

        this.getNotes(true);
        this.createPoll();
    }
    
    onReportFilterChanged() {
        if (this.filterReportId)
            this.params.report = this.filterReportId;
        else
            delete this.params.report;
    
        this.getNotes(true);
	}


	onNoteTimeClicked(event: { rowIndex: number, note: Note, href: string }) {
        this.$location.path('/note-detail')
            .search('note', event.href)
            .search('log', event.note.logId);

        if (this.showPendingSignoffOnly) {
            this.$location.search('query', JSON.stringify({
                log: event.note.logId,
                sort: ['timeframe,desc', 'id,desc'],
                signoffPendingFor: this.appStateService.currentUser?.id,
	            updatedOrTimeframeEnded: this.getEarliestSignoffTimeframe(),
            }))
                .search('return-location', 'notes')
                .search('index', event.rowIndex);
        }
    }
	
	onTimeframeFilterChanged(filter: { matchMode?: FilterMatchMode; date: Date | null }) {
		delete this.params.fromTimeframe;
		delete this.params.thruTimeframe;
		
		if (filter.date) {
			const date = filter.matchMode == FilterMatchMode.DATE_AFTER ? 'fromTimeframe' : 'thruTimeframe';

			this.params[date] = filter.date.toISOString();
		}

		this.getNotes(true);
	}

	onTurnoverFilterChanged(turnoverOnly: boolean) {
		if (turnoverOnly)
			this.params.turnover = true;
		else
			delete this.params.turnover;
		
		this.getNotes(true);
	}
	
    onScrollPageReached(scrollTop: number) {
			if (this.loading || !this.hasMoreNotes) return;
			
			//If lastId is not available, fallback to using the id of the last note within the list.
			const beforeNoteId = this.lastId ?? this.notes[this.notes.length - 1]?.id;
			if (beforeNoteId == null || beforeNoteId < 1) {
				throw Error(`beforeNoteId is invalid for fetching : ${beforeNoteId}`);
			}
		
			const params = this.usingTimeframeDesc ?
				{ beforeNoteId: beforeNoteId } :
				{ page: this.page += 1 };
	
	        this.getNotes(false, params, (success, notes) => {
						if (notes != null) {
							this.lastId = notes[notes.length - 1]?.id;
						}
	          if (success === false && !this.usingTimeframeDesc)
	            this.page -= 1;
	        });
    }
	
	refreshNote(note: DisplayNote<Note> | Note) {
		return this.noteService.getLogNote(note.id, NoteProjections.BASE).pipe(
			map(newNote => {
				if (!newNote) return null;
				const display = DisplayNote.setAbridgedNote(newNote);
				DisplayNote.updateShowing(display, note);
				return display;
			}));
	}
    
    getNotes(reset = false, params: Note.Query = {}, onComplete?: (success: boolean | null, notes?: NoteResponse<Note>) => void ) {
        if (reset) {
						this.lastId = undefined;
            this.notes = [];
            this.page = 0;
            this.loading = false;
            this.hasMoreNotes = true;
        }

		if (this.loading) {
			onComplete?.(null);
        	return;
        }
	
	    params = { ...params, ...this.params }
	
	    if (!params.log) // Do not allow note fetching without log parameter set
		    return;
        
		this.subscriptions.notesSubscription?.unsubscribe();
	    this.loading = true;
        
        this.subscriptions.notesSubscription = this.noteService.getLogNotes(params)
            .pipe(
                catchError(err => {
                    if (onComplete) {
                    	onComplete(false);
                    }
                    return throwError(err);
                }),
	            finalize(() => {
	            	this.loading = false;
	            })
            )
			.subscribe(notes => {
				const totalNotes = this.notes.length + notes.page.totalElements;

                if ('beforeNoteId' in params)
                    this.hasMoreNotes = notes.page.totalElements >= notes.page.size
                else
                    this.hasMoreNotes = this.page < notes.page.totalPages - 1

				this.mergeNotes(NotesTableDataRow.fromNotes(notes));
				this.notesMessage = `Showing ${this.notes.length}/${totalNotes} notes`;
	
				onComplete?.(true, notes);
			});
    }

	resetParams() {
        this.params = {
            log: this.logId,
            sort: ['timeframe,desc'],
			size: 50,
			...this.turnoverFilter && { turnover: true },
			...this.filterReportId && { report: this.filterReportId },
			...this.showPendingSignoffOnly && { signoffPendingFor: this.appStateService.currentUser?.id }
        }
    }

    createPoll() {
        this.subscriptions.notesPoll?.unsubscribe();

        if (!this.usingTimeframeDesc)
            return;

        let newest: Date | undefined;
		this.subscriptions.notesPoll = this.poll$.subscribe(notes => {
		    notes.sort(Utils.createSortFunction(n => new Date(n.created), 'desc'));

			const fetchNewest = new Date(notes[0]?.created);

			this.mergeNotes(notes);

			if (fetchNewest && !newest) {
			    newest = fetchNewest;
            }
			else if (newest && fetchNewest > newest) {
			    newest = fetchNewest;
			    this.messageService.add({
                    severity: 'info',
                    summary: 'New notes have arrived'
                })
            }
		});
	}
	
	getPDFReportQueryParams = () => ({
		...this.params.fromTimeframe && { From: Utils.formatDateString(this.params.fromTimeframe) },
		...this.params.thruTimeframe && { Through: Utils.formatDateString(this.params.thruTimeframe) },
		...this.params.turnover && { 'Turnover Only': 'True' },
		Sort: this.params.sort.map((s, i, array) => {
			const sortMap: { [sort: string]: string } = {
				maxPriority: 'Priority',
				timeframe: 'Timeframe',
				'createdBy.lastName': 'Source'
			},
			sort = s.split(','),
			number = array.length > 1 ? i + 1 + '. ' : '';

			return `${number}${sortMap[sort[0]]} ${sort[1] == 'asc' ? 'Ascending' : 'Descending'}`;
		}).join('\n')
	})

    private getNoteParams(this: EnterNotesTableDelegate) {
        const params = {
            log: this.logId,
            sort: ['timeframe,desc', 'updated,desc', 'id,desc'],
            size: 50
        } as Note.Query;

        if (this.updatedOrTimeframeEnded)
            params.updatedOrTimeframeEnded = this.updatedOrTimeframeEnded;

        if (this.filterReportId)
            params.report = this.filterReportId;
        
        if (this.showPendingSignoffOnly)
            params.signoffPendingFor = this.appStateService.currentUser?.id

        return params;
    }

    private mergeNotes(notes: NotesTableDataRow[]): NotesTableDataRow[] {
		const oldNoteMap = this.notes.reduce((map, note, idx) => {
			map[note.id] = { note, idx };
			return map;
		}, {} as Record<string | number, { note: Note, idx: number}>)
		
		const newNotes = notes.filter(newNote => {
			const oldNote = oldNoteMap[newNote.id];
			if (!oldNote) return true;
			
			DisplayNote.updateShowing(newNote, oldNote.note);
			this.notes.splice(oldNote.idx, 1, newNote);
			
			return false;
		})
		

		return this.notes = this.notes.concat(newNotes);
    }
    
    private fromTimeframeFilter(this: EnterNotesTableDelegate, note: Note): boolean {
        const noteDate = new Date(note.timeframe).valueOf()
        const usingTime = new Date(note.carryForwardEndTime ?? note.timeframe).valueOf()
        return usingTime > this.minTimeframe || usingTime === this.minTimeframe || (
            (noteDate < this.minTimeframe || noteDate === this.minTimeframe) &&
            note.carryForward && note.carryForwardEndTime === null
        )
    }
    
    private createBasePoll(): Observable<NotesTableDataRow[]> {
        let lastMaxUpdated: Date | undefined
        let currentMaxUpdated: Date | undefined
        
        return forkJoin({
            earliestNote: this.noteService.getNoteTimeBoundary(this.logId, 'earliest', this.filterReportId),
            latestNote: this.noteService.getNoteTimeBoundary(this.logId, 'latest', this.filterReportId)
        })
		.pipe(
			tap(noteTimes => {
				this.minTimeframe = noteTimes.earliestNote ? new Date(noteTimes.earliestNote).valueOf() : -1;
				lastMaxUpdated = noteTimes.latestNote ? new Date(noteTimes.latestNote) : undefined;
			}),
			mergeMap(() => this.noteService.createNewNotesPoll(() => {
				let maxIsoString = lastMaxUpdated?.toISOString();
				if (maxIsoString == null) {
					throw new Error('lastMaxUpdated is null');
				}
				
				return {
					...this.getNoteParams(),
					updatedOrTimeframeEnded: maxIsoString
				}
			})),
			map((n: any | Note[]) => {
		
				if (!Array.isArray(n)) {
					
					if (this.lastUpdate)
						this.lastUpdate.success = false;
					
					return [] as NotesTableDataRow[];
				}
		
				n.forEach((n: Note) => {
					const timeFrame = new Date(n.timeframe).valueOf()
					if (this.minTimeframe < 0 || timeFrame < this.minTimeframe)
						this.minTimeframe = timeFrame.valueOf()
			
					if (!currentMaxUpdated || Utils.getTime(n.updatedOrTimeframeEnded) > currentMaxUpdated.valueOf())
						currentMaxUpdated = new Date(n.updatedOrTimeframeEnded)
				})
		
				n = n.filter(this.fromTimeframeFilter.bind(this))
		
				this.lastUpdate = { date: new Date(), success: true };
		
				if (lastMaxUpdated && currentMaxUpdated && lastMaxUpdated.valueOf() == currentMaxUpdated.valueOf()) {
					currentMaxUpdated.setTime(currentMaxUpdated.valueOf() + 1)
				}
		
				if (currentMaxUpdated != null) {
					lastMaxUpdated = currentMaxUpdated
				}
		
				return NotesTableDataRow.fromNotes(n);
			}),
			share()
		)
    }
    
    private getEarliestSignoffTimeframe() {
	    return new Date(this.firstSignoffNoteTime ?? this.minTimeframe).toISOString()
    }
    
    private createSignoffCountPoll(): Observable<number> {
        let current = 0;
	    let initial = true;
	    let prevOnShift = this.shiftsService.isOnShift;
    
        return this._signoffCount$ = this.poll$.pipe(
            map(notes => {
                return initial || notes.some(n => n.hasSignoffData && this.fromTimeframeFilter(n));
            }),
            exhaustMap((shouldRefresh: boolean) => {
	            const shiftHasChanged = prevOnShift !== this.shiftsService.isOnShift;
	            prevOnShift = this.shiftsService.isOnShift;
	            
	            initial = false;
	            
                if (!shouldRefresh && !shiftHasChanged)
                	return of(current)
	            
                return getPendingSignoffs.bind(this)(shiftHasChanged)
            }),
            tap(count => current = count)
        )
    
    
        function getPendingSignoffs(this: EnterNotesTableDelegate, fullRefresh = false) {
            const params: Note.Query = {
                'log': LogService.selectedLog?.id,
                'sort': ['updated,asc', 'id,asc'],
                'size': 1,
                'signoffPendingFor': this.appStateService.currentUser?.id
            }
        
            if ((this.firstSignoffNoteTime || this.minTimeframe > -1) && !fullRefresh)
                params.updatedFromDate = this.getEarliestSignoffTimeframe();
        
            return this.noteService.getLogNotes(params)
                .pipe(
                    tap(notes => {
                        this.firstSignoffNoteTime = notes[0]?.updated
                    }),
                    map(notes => notes.page.totalElements)
                )
        }
	}
}